import { Avatar, Box, IconButton, Tooltip, Typography } from '@mui/material';
import FontAwesomeSvgIcon from 'components/shared/FontAwesomeSvgIcon';
import {
  GRAY_LIGHT_SECONDARY,
  GRAY_SECONDARY,
  PRIMARY,
  PRIMARY_LIGHT
} from 'constants/colors';
import dayjs from 'dayjs';
import { faInbox, faTrash } from 'vendors/@fortawesome/pro-light-svg-icons';
import ConfirmationDialog from 'components/shared/ConfirmationDialog';
import { useState } from 'react';
import { t } from 'i18next';
import useRemoveRequestNote from '../hooks/useRemoveRequestNote';

const NoteList = ({ notes, isFetching }) => {
  const removeNoteMutation = useRemoveRequestNote();

  const [selectedNoteId, setSelectedNoteId] = useState(null);
  const [isDialogOpen, setDialogOpen] = useState(false);

  const handleDeleteNote = () => {
    if (selectedNoteId !== null) {
      removeNoteMutation.mutate(selectedNoteId);
      setSelectedNoteId(null);
    }
  };

  const handleOpenDialog = (noteId) => {
    setSelectedNoteId(noteId);
    setDialogOpen(true);
  };

  const handleCloseDialog = (isConfirmed) => {
    setDialogOpen(false);
    if (isConfirmed) {
      handleDeleteNote();
    } else {
      setSelectedNoteId(null);
    }
  };

  return (
    <>
      {isDialogOpen && (
        <ConfirmationDialog
          title={t('deleteNote')}
          type="warning"
          handleAction={handleCloseDialog}
          cancelBtnText={t('cancel')}
          confirmBtnText={t('delete')}
        >
          <p>{t('confirmDeleteNote')}</p>
        </ConfirmationDialog>
      )}
      {isFetching ? (
        <p>Loading...</p>
      ) : notes?.length === 0 ? (
        <Box
          display="flex"
          flexDirection="column"
          gap={2}
          alignItems="center"
          mb={2}
        >
          <FontAwesomeSvgIcon icon={faInbox} fontSize="large" color="primary" />
          <Typography
            variant="h4"
            sx={{ fontSize: '16px' }}
            align="center"
            color="textSecondary"
          >
            {t('noNotesMessage')}
          </Typography>
        </Box>
      ) : (
        <Box width={1} maxHeight={350} overflow="auto">
          {notes
            ?.slice()
            .reverse()
            .map((note, index) => {
              const isLastItem = index === notes.length - 1;
              const formattedDate = dayjs(note.creationDate).format(
                'DD MMM. YYYY | HH:mm'
              );
              const firstLetter = note.creationUser.charAt(0).toUpperCase();

              return (
                <Box
                  display="flex"
                  justifyContent="space-between"
                  key={note.noteId}
                  paddingY={2}
                  borderBottom={
                    !isLastItem ? `1px solid ${GRAY_LIGHT_SECONDARY}` : 'none'
                  }
                >
                  <Box display="flex" alignItems="start" gap={2}>
                    <Avatar
                      sx={{
                        bgcolor: PRIMARY_LIGHT,
                        color: PRIMARY,
                        width: 32,
                        height: 32,
                        fontSize: '14px'
                      }}
                    >
                      {firstLetter}
                    </Avatar>
                    <Box display="flex" flexDirection="column">
                      <Typography
                        variant="h4"
                        component="h4"
                        sx={{ fontSize: '16px' }}
                      >
                        {note.creationUser}
                      </Typography>
                      <Box
                        color={GRAY_SECONDARY}
                        display="flex"
                        gap={1}
                        marginBottom={1.25}
                      >
                        {formattedDate}
                      </Box>
                      {note.content}
                    </Box>
                  </Box>
                  <Box
                    display="flex"
                    flexDirection="column"
                    alignItems="end"
                    gap={0.5}
                  >
                    <Tooltip title={t('deleteNote')} arrow>
                      <IconButton
                        onClick={() => handleOpenDialog(note.noteId)}
                        size="small"
                        color="default"
                        sx={{ maxWidth: 'max-content' }}
                        disabled={removeNoteMutation.isLoading}
                      >
                        <FontAwesomeSvgIcon icon={faTrash} fontSize="small" />
                      </IconButton>
                    </Tooltip>
                  </Box>
                </Box>
              );
            })}
        </Box>
      )}
    </>
  );
};

export default NoteList;
